/**
 * capabilities.ts — Browser HW codec + display probe.
 *
 * Detects what the client's WebCodecs implementation can decode AND
 * what the display can show. The result is sent to zapd at session
 * start so the server can decide whether to passthrough the source
 * stream (= no transcoding, instant playback) or transcode to a
 * supported codec.
 *
 * Per Zappiti / Plex direct-play model: when the client has the
 * native HW decoder, the server should never re-encode. This is the
 * 99.7% case in 2026 (= HEVC + AV1 covers virtually all browsers).
 *
 * The 0.3% (= Edge Windows without HEVC extension, ancient browsers)
 * fall back to the WASM-decode path or to legacy server-transcode.
 *
 * Codec strings reference (= ISO/IEC 14496-15 + ITU-T H.265 + AV1 spec):
 *   - HEVC Main 8 / Lvl 4.0 (1080p):  hev1.1.6.L120.B0
 *   - HEVC Main 8 / Lvl 5.1 (4K):     hev1.1.6.L153.B0
 *   - HEVC Main10 / Lvl 5.1 (4K HDR): hev1.2.4.L153.B0
 *   - AV1 Main / Lvl 5.1:             av01.0.12M.08
 *   - AV1 Main10 / Lvl 5.1:           av01.0.13M.10
 *   - H.264 High@4.0 (1080p):         avc1.640028
 *   - H.264 High@5.1 (4K):            avc1.640033
 *
 * Audio codec strings:
 *   - AAC-LC:        mp4a.40.2
 *   - AAC-HE:        mp4a.40.5
 *   - AAC-HEv2:      mp4a.40.29
 *   - AC-3:          ac-3
 *   - E-AC-3:        ec-3
 *   - Opus:          opus
 *   - FLAC:          flac
 *   - TrueHD:        — not via WebCodecs (WASM-only)
 *   - DTS / DTS-HD:  — not via WebCodecs (WASM-only)
 */

export interface VideoCodecCaps {
  /** Hardware-accelerated decode available. */
  hw: boolean;
  /** Software fallback available (= can decode, but slower). */
  sw: boolean;
}

export interface VideoCaps {
  h264:        VideoCodecCaps;
  hevcMain:    VideoCodecCaps;  // 8-bit
  hevcMain10:  VideoCodecCaps;  // 10-bit (= UHD-BluRay HDR)
  av1Main:     VideoCodecCaps;  // 8-bit
  av1Main10:   VideoCodecCaps;
}

export interface AudioCaps {
  aac:    boolean;
  ac3:    boolean;
  eac3:   boolean;
  opus:   boolean;
  flac:   boolean;
  // TrueHD/DTS not in WebCodecs — WASM fallback handled separately.
}

export interface DisplayCaps {
  /** Display can show HDR (= dynamic-range: high media query). */
  hdr: boolean;
  /** Display covers wide gamut (= color-gamut: rec2020 or p3). */
  wideGamut: boolean;
  /** Width × height of the device's primary screen (px). */
  pixelWidth:  number;
  pixelHeight: number;
}

export interface ClientCaps {
  video:   VideoCaps;
  audio:   AudioCaps;
  display: DisplayCaps;
  /** True if WebCodecs API is available at all. Without it, fall back
   *  to legacy <video>+MSE path (= server must transcode). */
  webCodecs: boolean;
}

interface VideoDecoderConfig {
  codec: string;
  hardwareAcceleration?: 'prefer-hardware' | 'prefer-software' | 'no-preference';
  codedWidth?: number;
  codedHeight?: number;
}

interface AudioDecoderConfig {
  codec: string;
  sampleRate?: number;
  numberOfChannels?: number;
}

declare global {
  interface Window {
    VideoDecoder?: {
      isConfigSupported(c: VideoDecoderConfig): Promise<{ supported: boolean }>;
    };
    AudioDecoder?: {
      isConfigSupported(c: AudioDecoderConfig): Promise<{ supported: boolean }>;
    };
  }
}

async function probeVideoCodec(codec: string, codedWidth = 1920, codedHeight = 1080): Promise<VideoCodecCaps> {
  if (typeof window.VideoDecoder?.isConfigSupported !== 'function') {
    return { hw: false, sw: false };
  }
  let hw = false, sw = false;
  try {
    const r = await window.VideoDecoder.isConfigSupported({
      codec, codedWidth, codedHeight, hardwareAcceleration: 'prefer-hardware',
    });
    hw = !!r.supported;
  } catch { /* hw probe rejected — codec not supported by HW path */ }
  try {
    const r = await window.VideoDecoder.isConfigSupported({
      codec, codedWidth, codedHeight, hardwareAcceleration: 'prefer-software',
    });
    sw = !!r.supported;
  } catch { /* sw probe rejected */ }
  return { hw, sw };
}

async function probeAudioCodec(codec: string): Promise<boolean> {
  if (typeof window.AudioDecoder?.isConfigSupported !== 'function') return false;
  try {
    const r = await window.AudioDecoder.isConfigSupported({
      codec, sampleRate: 48000, numberOfChannels: 2,
    });
    return !!r.supported;
  } catch { return false; }
}

function probeDisplay(): DisplayCaps {
  const safeMatch = (q: string): boolean => {
    try { return window.matchMedia(q).matches; } catch { return false; }
  };
  return {
    hdr:        safeMatch('(dynamic-range: high)'),
    wideGamut:  safeMatch('(color-gamut: rec2020)') || safeMatch('(color-gamut: p3)'),
    pixelWidth:  Math.floor(window.screen?.width  || window.innerWidth  || 1920),
    pixelHeight: Math.floor(window.screen?.height || window.innerHeight || 1080),
  };
}

/**
 * Probe all video + audio + display capabilities in parallel.
 * Total wall-clock: ~50-100 ms (= each isConfigSupported call is fast).
 * Cached at module-level after first call so re-attach doesn't re-probe.
 */
let cached: Promise<ClientCaps> | null = null;

export function probeClientCaps(): Promise<ClientCaps> {
  if (cached) return cached;
  cached = (async (): Promise<ClientCaps> => {
    const webCodecs = typeof window.VideoDecoder !== 'undefined';
    if (!webCodecs) {
      return {
        webCodecs: false,
        display:   probeDisplay(),
        video: {
          h264:       { hw: false, sw: false },
          hevcMain:   { hw: false, sw: false },
          hevcMain10: { hw: false, sw: false },
          av1Main:    { hw: false, sw: false },
          av1Main10:  { hw: false, sw: false },
        },
        audio: { aac: false, ac3: false, eac3: false, opus: false, flac: false },
      };
    }
    const [
      h264, hevcMain, hevcMain10, av1Main, av1Main10,
      aac, ac3, eac3, opus, flac,
    ] = await Promise.all([
      probeVideoCodec('avc1.640028', 1920, 1080),
      probeVideoCodec('hev1.1.6.L153.B0', 3840, 2160),
      probeVideoCodec('hev1.2.4.L153.B0', 3840, 2160),
      probeVideoCodec('av01.0.12M.08',    1920, 1080),
      probeVideoCodec('av01.0.13M.10',    3840, 2160),
      probeAudioCodec('mp4a.40.2'),
      probeAudioCodec('ac-3'),
      probeAudioCodec('ec-3'),
      probeAudioCodec('opus'),
      probeAudioCodec('flac'),
    ]);
    return {
      webCodecs: true,
      video:   { h264, hevcMain, hevcMain10, av1Main, av1Main10 },
      audio:   { aac, ac3, eac3, opus, flac },
      display: probeDisplay(),
    };
  })();
  return cached;
}

/**
 * Format the capability summary as a single console-friendly line.
 * Useful for diagnostic logs at attach-time.
 */
export function formatCapsSummary(c: ClientCaps): string {
  const vid = (k: keyof VideoCaps): string => {
    const v = c.video[k];
    if (v.hw) return 'HW';
    if (v.sw) return 'SW';
    return '-';
  };
  const aud = Object.entries(c.audio)
    .filter(([_, v]) => v).map(([k]) => k).join('/') || '-';
  const dispFlags: string[] = [];
  if (c.display.hdr) dispFlags.push('HDR');
  if (c.display.wideGamut) dispFlags.push('WG');
  return `h264=${vid('h264')} hevc=${vid('hevcMain')} hevc10=${vid('hevcMain10')} ` +
         `av1=${vid('av1Main')} av1-10=${vid('av1Main10')} ` +
         `audio=${aud} display=${c.display.pixelWidth}x${c.display.pixelHeight}` +
         (dispFlags.length > 0 ? ` [${dispFlags.join(',')}]` : '');
}
